import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Autocomplete, Box, Button, Chip, IconButton, Stack, Table, TableBody, TableCell,
    TableFooter, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import EnergyApiService from '../../api/EnergyApiService';
import AdjacentPeriodRow from './AdjacentPeriodRow';
import SaveButton from '../Common/SaveButton';
import { useSnackbar } from '../Common/SnackbarProvider';
import { useAsyncCallback } from '../../hooks/useAsyncCallback';
import {
    ANOMALY_TOLERANCE, ISO_DATE, adjacentPeriod, formatQuantity, isRowDirty, medianRate, monthsInRange,
    newRowKey, parseQuantity, periodDays, periodIssues, rateDeviation, widenRange,
} from './energyEntryUtils';

const energyApiService = new EnergyApiService();

const OUTPUT_UNIT_SUGGESTIONS = ['tons', 'tonnes', 'units', 'cases', 'lb', 'kg', 'gallons'];

const DEFAULT_OUTPUT_UNIT = 'tons';

function toRow (record) {
    const saved = {
        periodStart: record.periodStart,
        periodEnd: record.periodEnd,
        quantity: record.quantity ?? '',
        soldQuantity: record.soldQuantity ?? '',
        outputUnit: record.outputUnit,
        notes: record.notes ?? '',
    };

    return { key: `id-${record.id}`, id: record.id, energySiteId: record.energySiteId, ...saved, saved };
}

/**
 * Production and sales output for a location, one row per reported period.
 *
 * Periods are real dates rather than month numbers: customers report on month boundaries today, but
 * the analysis overlaps production with utility periods, which never line up with a calendar month.
 */
export default function ProductionEntryGrid ({ projectId, siteId, from, to, canEdit }) {
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [outputUnit, setOutputUnit] = useState(DEFAULT_OUTPUT_UNIT);

    // Starts as the chosen window and widens as earlier or later periods are pulled into the grid,
    // so the reload after a save keeps them on screen. A ref rather than state: widening must not
    // itself reload, or the reload would throw away the blank row that was just added.
    const rangeRef = useRef({ from, to });

    const load = useCallback(async () => {
        if (!projectId) return;

        setLoading(true);
        try {
            const response = await energyApiService.getProduction(projectId, rangeRef.current.from, rangeRef.current.to, siteId);
            if (!response?.success) {
                snackbar.error(response?.message ?? t('siteData.toast.loadFailed'));
                return;
            }

            const loaded = response.data.map(toRow);
            setRows(loaded);
            if (loaded.length > 0) {
                setOutputUnit(loaded[loaded.length - 1].outputUnit);
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, siteId, from, to]);

    // A new window or site starts from the chosen window again; the reload after a save does not.
    useEffect(() => {
        rangeRef.current = { from, to };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    const updateRow = (key, field, value) => {
        setRows(current => current.map(row => (row.key === key ? { ...row, [field]: value } : row)));
    };

    const addMissingMonths = () => {
        const covered = rows.filter(row => row.periodStart && row.periodEnd);
        const missing = monthsInRange(from, to).filter(month =>
            !covered.some(row => row.periodStart <= month.periodEnd && row.periodEnd >= month.periodStart));

        if (missing.length === 0) {
            snackbar.success(t('siteData.production.noMissingMonths'));
            return;
        }

        setRows(current => [
            ...current,
            ...missing.map(month => ({
                key: newRowKey(),
                id: null,
                energySiteId: siteId ?? null,
                periodStart: month.periodStart,
                periodEnd: month.periodEnd,
                quantity: '',
                soldQuantity: '',
                outputUnit,
                notes: '',
                saved: null,
            })),
        ]);
    };

    const blankRow = period => ({
        key: newRowKey(),
        id: null,
        energySiteId: siteId ?? null,
        periodStart: period.periodStart,
        periodEnd: period.periodEnd,
        quantity: '',
        soldQuantity: '',
        outputUnit,
        notes: '',
        saved: null,
    });

    /**
     * Adds the period before the first row or after the last: the adjacent calendar month when the
     * rows sit on month boundaries. Figures already saved for that span are pulled in as they are;
     * otherwise a blank row is added.
     */
    const { run: addAdjacent, loading: adding } = useAsyncCallback(async direction => {
        const ownRows = rows.filter(row => row.energySiteId === (siteId ?? null));
        const period = adjacentPeriod(ownRows, direction, { fallbackStart: rangeRef.current.from, monthly: true });

        const response = await energyApiService.getProduction(projectId, period.periodStart, period.periodEnd, siteId);
        if (!response?.success) {
            snackbar.error(response?.message ?? t('siteData.toast.loadFailed'));
            return;
        }

        const known = new Set(rows.filter(row => row.id !== null).map(row => row.id));
        const found = response.data.filter(record => !known.has(record.id)).map(toRow);
        const foundOwn = found.filter(row => row.energySiteId === (siteId ?? null));

        setRows(current => [
            ...current,
            ...found,
            ...(foundOwn.length === 0 ? [blankRow(period)] : []),
        ]);
        rangeRef.current = [period, ...found].reduce(widenRange, rangeRef.current);

        if (foundOwn.length > 0) {
            snackbar.info(t('siteData.toast.loadedExisting', { count: foundOwn.length }));
        }
    });

    const removeRow = async (row) => {
        if (row.id === null) {
            setRows(current => current.filter(r => r.key !== row.key));
            return;
        }

        const response = await energyApiService.deleteProductionRecord(row.id);
        if (!response?.success) {
            snackbar.error(response?.message ?? t('siteData.toast.deleteFailed'));
            return;
        }

        snackbar.success(t('siteData.production.toast.deleted'));
        setRows(current => current.filter(r => r.key !== row.key));
    };

    const sortedRows = useMemo(
        () => rows.slice().sort((a, b) => (a.periodStart || '9999').localeCompare(b.periodStart || '9999')),
        [rows]);

    const dirtyRows = useMemo(() => rows.filter(isRowDirty), [rows]);
    const issues = useMemo(() => periodIssues(rows.filter(row => row.energySiteId === (siteId ?? null))), [rows, siteId]);
    const median = useMemo(() => medianRate(rows.map(row => ({ ...row, consumption: row.quantity }))), [rows]);

    const blocked = useMemo(
        () => Object.values(issues).some(issue => issue.type === 'overlap' || issue.type === 'outOfOrder'),
        [issues]);

    const { run: save, loading: saving } = useAsyncCallback(async () => {
        const invalid = dirtyRows.find(row =>
            !row.periodStart || !row.periodEnd
            || Number.isNaN(parseQuantity(row.quantity)) || Number.isNaN(parseQuantity(row.soldQuantity)));

        if (invalid) {
            snackbar.error(t('siteData.toast.checkRows'));
            return;
        }

        const response = await energyApiService.saveProduction(projectId, dirtyRows.map(row => ({
            id: row.id,
            energySiteId: row.energySiteId,
            periodStart: row.periodStart,
            periodEnd: row.periodEnd,
            quantity: parseQuantity(row.quantity),
            soldQuantity: parseQuantity(row.soldQuantity),
            outputUnit: row.outputUnit || outputUnit,
            notes: row.notes || null,
        })));

        if (!response?.success) {
            snackbar.error(response?.message ?? t('siteData.toast.saveFailed'));
            return;
        }

        snackbar.success(t('siteData.production.toast.saved', { count: dirtyRows.length }));
        await load();
    });

    const totals = useMemo(() => rows.reduce((sum, row) => {
        const produced = parseQuantity(row.quantity);
        const sold = parseQuantity(row.soldQuantity);
        return {
            produced: sum.produced + (produced && !Number.isNaN(produced) ? produced : 0),
            sold: sum.sold + (sold && !Number.isNaN(sold) ? sold : 0),
        };
    }, { produced: 0, sold: 0 }), [rows]);

    const issueFor = (row) => {
        const issue = issues[row.key];
        if (!issue) return null;

        if (issue.type === 'overlap') return t('siteData.issue.overlap');
        if (issue.type === 'outOfOrder') return t('siteData.issue.outOfOrder');
        return t('siteData.issue.gap', { count: issue.days });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" color="text.secondary">
                        {t('siteData.production.producedTotal', { total: formatQuantity(totals.produced, 0), unit: outputUnit })}
                        {' · '}
                        {t('siteData.production.soldTotal', { total: formatQuantity(totals.sold, 0), unit: outputUnit })}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {canEdit && (
                        <Button size="small" startIcon={<AddIcon />} onClick={addMissingMonths}>
                            {t('siteData.production.addMissingMonths')}
                        </Button>
                    )}
                </Stack>

                {blocked && <Alert severity="warning">{t('siteData.issue.blockedSave')}</Alert>}

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 190 }}>{t('siteData.column.periodStart')}</TableCell>
                            <TableCell sx={{ width: 190 }}>{t('siteData.column.periodEnd')}</TableCell>
                            <TableCell align="right" sx={{ width: 70 }}>{t('siteData.column.days')}</TableCell>
                            <TableCell align="right" sx={{ width: 150 }}>{t('siteData.column.produced')}</TableCell>
                            <TableCell align="right" sx={{ width: 150 }}>{t('siteData.column.sold')}</TableCell>
                            <TableCell sx={{ width: 130 }}>{t('siteData.column.unit')}</TableCell>
                            <TableCell align="right" sx={{ width: 130 }}>{t('siteData.column.netToInventory')}</TableCell>
                            <TableCell>{t('siteData.column.notes')}</TableCell>
                            <TableCell sx={{ width: 56 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {canEdit && (
                            <AdjacentPeriodRow
                                label={t('siteData.action.addEarlier')}
                                icon={<ArrowUpwardIcon fontSize="small" />}
                                disabled={adding}
                                onClick={() => addAdjacent('before')}
                            />
                        )}
                        {sortedRows.map(row => {
                            const days = periodDays(row.periodStart, row.periodEnd);
                            const produced = parseQuantity(row.quantity);
                            const sold = parseQuantity(row.soldQuantity);
                            const net = produced !== null && sold !== null && !Number.isNaN(produced) && !Number.isNaN(sold)
                                ? produced - sold
                                : null;
                            const deviation = rateDeviation(row, median, 'quantity');
                            const flagged = deviation !== null && Math.abs(deviation) > ANOMALY_TOLERANCE;
                            const issue = issueFor(row);
                            const locationWide = row.energySiteId === null && siteId;

                            return (
                                <TableRow key={row.key} sx={{ backgroundColor: isRowDirty(row) ? 'rgba(153, 194, 14, 0.08)' : undefined }}>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <DesktopDatePicker
                                                inputFormat="YYYY-MM-DD"
                                                value={row.periodStart ? dayjs(row.periodStart) : null}
                                                disabled={!canEdit}
                                                onChange={value => updateRow(row.key, 'periodStart', value ? value.format(ISO_DATE) : '')}
                                                renderInput={params => <TextField {...params} size="small" variant="standard" />}
                                            />
                                            {locationWide && <Chip size="small" label={t('siteData.production.locationWide')} />}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <DesktopDatePicker
                                            inputFormat="YYYY-MM-DD"
                                            value={row.periodEnd ? dayjs(row.periodEnd) : null}
                                            disabled={!canEdit}
                                            onChange={value => updateRow(row.key, 'periodEnd', value ? value.format(ISO_DATE) : '')}
                                            renderInput={params => <TextField {...params} size="small" variant="standard" />}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{days ?? '—'}</TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            size="small"
                                            variant="standard"
                                            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                                            value={row.quantity}
                                            disabled={!canEdit}
                                            error={Number.isNaN(produced)}
                                            onChange={event => updateRow(row.key, 'quantity', event.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            size="small"
                                            variant="standard"
                                            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                                            value={row.soldQuantity}
                                            disabled={!canEdit}
                                            error={Number.isNaN(sold)}
                                            onChange={event => updateRow(row.key, 'soldQuantity', event.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Autocomplete
                                            freeSolo
                                            size="small"
                                            disabled={!canEdit}
                                            options={OUTPUT_UNIT_SUGGESTIONS}
                                            value={row.outputUnit ?? ''}
                                            onInputChange={(event, value) => updateRow(row.key, 'outputUnit', value)}
                                            renderInput={params => <TextField {...params} variant="standard" />}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                            {flagged && (
                                                <Tooltip title={t('siteData.issue.offMedian', { percent: Math.round(deviation * 100) })}>
                                                    <WarningAmberIcon fontSize="small" color="warning" />
                                                </Tooltip>
                                            )}
                                            <span>{net === null ? '—' : formatQuantity(net, 1)}</span>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={0.5}>
                                            <TextField
                                                size="small"
                                                variant="standard"
                                                fullWidth
                                                value={row.notes}
                                                disabled={!canEdit}
                                                onChange={event => updateRow(row.key, 'notes', event.target.value)}
                                            />
                                            {issue && <Typography variant="caption" color="warning.dark">{issue}</Typography>}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {canEdit && (
                                            <IconButton
                                                size="small"
                                                aria-label={t('siteData.action.deleteRow')}
                                                onClick={() => removeRow(row)}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {rows.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={9}>
                                    <Typography variant="body2" color="text.secondary">{t('siteData.production.empty')}</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {canEdit && rows.length > 0 && (
                            <AdjacentPeriodRow
                                label={t('siteData.action.addLater')}
                                icon={<ArrowDownwardIcon fontSize="small" />}
                                disabled={adding}
                                onClick={() => addAdjacent('after')}
                            />
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={3}>{t('siteData.column.total')}</TableCell>
                            <TableCell align="right">{formatQuantity(totals.produced, 1)}</TableCell>
                            <TableCell align="right">{formatQuantity(totals.sold, 1)}</TableCell>
                            <TableCell colSpan={4} />
                        </TableRow>
                    </TableFooter>
                </Table>

                {canEdit && (
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            {dirtyRows.length > 0 ? t('siteData.unsavedRows', { count: dirtyRows.length }) : ''}
                        </Typography>
                        <Button disabled={dirtyRows.length === 0 || saving} onClick={load}>{t('siteData.action.discard')}</Button>
                        <SaveButton
                            variant="contained"
                            loading={saving}
                            disabled={dirtyRows.length === 0 || blocked}
                            onClick={save}
                        >
                            {t('siteData.action.save')}
                        </SaveButton>
                    </Stack>
                )}
            </Stack>
        </LocalizationProvider>
    );
}

ProductionEntryGrid.propTypes = {
    projectId: PropTypes.number,
    siteId: PropTypes.number,
    /** Inclusive ISO dates the grid opens on; it widens past them as periods are added. */
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    canEdit: PropTypes.bool,
};
